import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export function usePoll(path, ms = 3000) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const alive = useRef(true);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(path);
      if (alive.current) { setData(data); setError(null); }
    } catch (e) {
      if (alive.current) setError(errMsg(e));
    }
  }, [path]);
  useEffect(() => {
    alive.current = true;
    load();
    const t = ms ? setInterval(load, ms) : null;
    return () => { alive.current = false; if (t) clearInterval(t); };
  }, [load, ms]);
  return { data, error, reload: load };
}

const HEALTH = {
  green: { c: "bg-emerald-500", t: "text-emerald-400", b: "border-emerald-500/30 bg-emerald-500/10", l: "ON PRIMARY" },
  amber: { c: "bg-amber-500", t: "text-amber-400", b: "border-amber-500/30 bg-amber-500/10", l: "ON BACKUP" },
  red: { c: "bg-red-500", t: "text-red-400", b: "border-red-500/30 bg-red-500/10", l: "FAILING" },
  grey: { c: "bg-slate-500", t: "text-slate-400", b: "border-slate-600 bg-slate-800/40", l: "STOPPED" },
};

export function StatusDot({ health = "grey", testid }) {
  const h = HEALTH[health] || HEALTH.grey;
  return <span data-testid={testid} className={`inline-block h-2.5 w-2.5 rounded-full ${h.c} ${health !== "grey" ? "dot-live" : ""} ${h.t}`} />;
}

export function HealthBadge({ health = "grey", status, testid }) {
  const h = HEALTH[health] || HEALTH.grey;
  const label = status === "off_air" ? "OFF AIR" : status === "starting" ? "STARTING" : h.l;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono font-medium border ${h.b} ${h.t}`}>
      <StatusDot health={health} />{label}
    </span>
  );
}

export function Pill({ children, tone = "slate", testid }) {
  const tones = {
    slate: "border-slate-700 bg-slate-800/60 text-slate-300",
    green: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-400",
    red: "border-red-500/30 bg-red-500/10 text-red-400",
    cyan: "border-cyan-500/30 bg-cyan-500/10 text-cyan-400",
    blue: "border-blue-500/30 bg-blue-500/10 text-blue-400",
  };
  return <span data-testid={testid} className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono border ${tones[tone]}`}>{children}</span>;
}

export function CopyButton({ text, testid, label }) {
  const [ok, setOk] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setOk(true); toast.success("Copied to clipboard");
    setTimeout(() => setOk(false), 1200);
  };
  return (
    <button data-testid={testid} onClick={copy} title="Copy"
      className="inline-flex items-center gap-1 px-1.5 py-1 rounded text-slate-400 hover:text-cyan-300 hover:bg-slate-800 transition-colors">
      {ok ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}{label && <span className="text-xs">{label}</span>}
    </button>
  );
}

export function UrlRow({ label, url, testid }) {
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-slate-800/70 last:border-0">
      <span className="w-20 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <code data-testid={testid} className="flex-1 truncate text-xs text-slate-300">{url}</code>
      <CopyButton text={url} testid={`${testid}-copy`} />
    </div>
  );
}

export function Panel({ title, right, children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`bg-[#0F1722] border border-slate-800 rounded-md fade-up ${className}`}>
      {title && (
        <header className="flex items-center justify-between px-4 h-10 border-b border-slate-800">
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 font-sans">{title}</h3>
          {right}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function PageHeader({ title, sub, right }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-100">{title}</h1>
        {sub && <p className="text-sm text-slate-400 mt-1">{sub}</p>}
      </div>
      <div className="flex items-center gap-2">{right}</div>
    </div>
  );
}

export function Stat({ label, value, tone, testid }) {
  return (
    <div className="bg-[#0B111B] border border-slate-800 rounded px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div data-testid={testid} className={`font-mono text-sm mt-0.5 ${tone || "text-slate-200"}`}>{value ?? "—"}</div>
    </div>
  );
}

export function Empty({ children }) {
  return <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-800 rounded">{children}</div>;
}

export const LEVEL_TONE = { crit: "red", warn: "amber", ok: "green", info: "slate" };
